import { useEffect, useRef, useState } from 'react';
import Icon from './Icon';
import { Button } from './ui';
import { createGuestLink, listGuestLinks, revokeGuestLink } from '@/lib/api';
import type { GuestLinkInfo, TripListItem } from '@/lib/types';

// The one share surface: a popover off the Share button that lists
// guest links and creates new ones with a scope picker (this trip
// / pick trips / all my trips). Mounted in two places:
//   - TripShell top bar (tripId set) — lists only the links that cover
//     this trip (its own links and all-trips links); "New guest link"
//     makes a this-trip link, and the wider scopes sit behind "More
//     options".
//   - TripsList header (no tripId) — lists every link, create defaults
//     to "Pick trips".
// Replaces the retired bottom-of-trips-list GuestLinksManager section.

type ScopeChoice = 'this' | 'trips' | 'all';

function describeScope(l: GuestLinkInfo, tripNames: Map<string, string>): string {
  if (l.scope === 'all') return 'All trips';
  if (l.scope === 'showcase') return `Showcase · ${tripNames.get(l.tripIds?.[0] ?? '') ?? l.tripIds?.[0] ?? ''}`;
  const names = (l.tripIds ?? []).map((id) => tripNames.get(id) ?? id);
  if (names.length <= 2) return names.join(' + ');
  return `${names[0]} + ${names.length - 1} more`;
}

// Matches the server (guest-grants.js): an all-trips link covers owner-role
// trips only and never a draft.
function covers(l: GuestLinkInfo, tripId: string, allCovers: boolean): boolean {
  return l.scope === 'all' ? allCovers : (l.tripIds ?? []).includes(tripId);
}

function shortUrl(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/(\/[gs]\/[a-f0-9]{6})[a-f0-9]+$/i, '$1…');
}

export default function SharePanel({
  tripId,
  trips,
}: {
  tripId?: string;
  trips: TripListItem[];
}) {
  const [open, setOpen] = useState(false);
  const [links, setLinks] = useState<GuestLinkInfo[] | null>(null);
  // Links the owner has that don't cover this trip (in-trip mode only).
  const [otherCount, setOtherCount] = useState(0);
  const [note, setNote] = useState<string | null>(null);
  const [moreScopes, setMoreScopes] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  // create form
  const [creating, setCreating] = useState(false);
  const [scope, setScope] = useState<ScopeChoice>(tripId ? 'this' : 'trips');
  const [picked, setPicked] = useState<Set<string>>(
    () => new Set(tripId ? [tripId] : []),
  );
  const [label, setLabel] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  const resetCreate = () => {
    setCreating(false);
    setMoreScopes(false);
    setScope(tripId ? 'this' : 'trips');
    setPicked(new Set(tripId ? [tripId] : []));
  };

  // Esc: inside the label field of the create form it cancels the form (the
  // popover stays); anywhere else it closes the popover and puts focus back
  // on the Share button that opened it.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || e.isComposing) return;
      const t = e.target as HTMLElement | null;
      if (creating && t?.tagName === 'INPUT' && (t as HTMLInputElement).type === 'text') {
        e.preventDefault();
        resetCreate();
        return;
      }
      e.preventDefault();
      setOpen(false);
      rootRef.current?.querySelector('button')?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // resetCreate only reads tripId, which is in the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, creating, tripId]);

  const owned = trips.filter((t) => t.role === 'owner');
  const tripNames = new Map(trips.map((t) => [t.tripId, t.name]));
  const thisTrip = tripId ? trips.find((t) => t.tripId === tripId) : undefined;
  const allCovers = thisTrip?.role === 'owner' && !thisTrip.draft;
  const isDraft = thisTrip?.draft === true;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);
    setNote(null);
    listGuestLinks()
      .then((res) => {
        if (cancelled) return;
        if (tripId) {
          const mine = res.links.filter((l) => covers(l, tripId, allCovers));
          setLinks(mine);
          setOtherCount(res.links.length - mine.length);
        } else {
          setLinks(res.links);
          setOtherCount(0);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load links.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, tripId, allCovers]);

  const copy = async (url: string, token: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(token);
      setTimeout(() => setCopied((c) => (c === token ? null : c)), 1600);
    } catch {
      setError('Could not copy — copy the URL manually.');
    }
  };

  const onCreate = async () => {
    const tripIds =
      scope === 'this' && tripId ? [tripId] : scope === 'trips' ? [...picked] : undefined;
    if (scope !== 'all' && (!tripIds || tripIds.length === 0)) {
      setError('Pick at least one trip for the link.');
      return;
    }
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const { link } = await createGuestLink({
        scope: scope === 'all' ? 'all' : 'trips',
        ...(tripIds ? { tripIds } : {}),
        ...(label.trim() ? { label: label.trim() } : {}),
      });
      const listed = !tripId || covers(link, tripId, allCovers);
      if (listed) {
        setLinks((prev) => [link, ...(prev ?? [])]);
      } else {
        setOtherCount((n) => n + 1);
      }
      setCreating(false);
      setMoreScopes(false);
      setScope(tripId ? 'this' : 'trips');
      setPicked(new Set(tripId ? [tripId] : []));
      setLabel('');
      await copy(link.url, link.token);
      if (!listed) setNote('Link created for other trips. It is listed on the trips page.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create link.');
    } finally {
      setBusy(false);
    }
  };

  const onRevoke = async (link: GuestLinkInfo) => {
    const what = link.scope === 'all' ? 'the all-trips link' : 'this link';
    if (!window.confirm(`Revoke ${what}? Anyone using it loses access immediately.`)) return;
    setBusy(true);
    setError(null);
    try {
      await revokeGuestLink(link.token);
      setLinks((prev) => (prev ?? []).filter((l) => l.token !== link.token));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke link.');
    } finally {
      setBusy(false);
    }
  };

  const radioRow: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    fontSize: 12.5,
    cursor: 'pointer',
    fontFamily: 'var(--font-ui)',
  };

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <Button variant="secondary" size="sm" icon="share" onClick={() => setOpen((o) => !o)}>
        Share
      </Button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
          <div
            style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: 8,
              width: 360,
              maxHeight: '70vh',
              overflowY: 'auto',
              zIndex: 41,
              background: 'var(--surface)',
              borderRadius: 'var(--r-md)',
              border: '1px solid var(--line)',
              boxShadow: 'var(--shadow-pop)',
              padding: 12,
              animation: 'wa-fade-up var(--dur) var(--ease)',
            }}
          >
            <div className="wa-eyebrow" style={{ padding: '0 2px 8px' }}>
              {tripId ? `Guest links · ${tripNames.get(tripId) ?? 'this trip'}` : 'Guest links'}
            </div>
            {links === null && !error && (
              <div style={{ fontSize: 12.5, color: 'var(--ink-mute)', padding: '4px 2px' }}>Loading…</div>
            )}
            {(links ?? []).map((l) => (
              <div
                key={l.token}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '7px 8px',
                  borderRadius: 8,
                  background: 'var(--surface-2)',
                  marginBottom: 6,
                }}
              >
                <Icon name="share" size={13} style={{ color: 'var(--ink-mute)', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {l.label || describeScope(l, tripNames)}
                  </div>
                  <div className="wa-mono" style={{ fontSize: 11, color: 'var(--ink-mute)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {describeScope(l, tripNames)} · {shortUrl(l.url)}
                  </div>
                </div>
                <button
                  onClick={() => copy(l.url, l.token)}
                  disabled={busy}
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 11.5, fontWeight: 600, color: 'var(--accent)', fontFamily: 'var(--font-ui)', padding: '2px 4px' }}
                >
                  {copied === l.token ? 'Copied' : 'Copy'}
                </button>
                <button
                  onClick={() => onRevoke(l)}
                  disabled={busy}
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 11.5, color: 'var(--ink-mute)', fontFamily: 'var(--font-ui)', padding: '2px 4px' }}
                >
                  Revoke
                </button>
              </div>
            ))}
            {links !== null && links.length === 0 && !creating && (
              <div style={{ fontSize: 12.5, color: 'var(--ink-mute)', padding: '2px 2px 8px' }}>
                {tripId
                  ? 'No guest links for this trip yet. Anyone with a link can view it — read-only, no account needed.'
                  : 'No guest links yet. Anyone with a link can view the covered trips — read-only, no account needed.'}
              </div>
            )}
            {links !== null && tripId && otherCount > 0 && (
              <div style={{ fontSize: 11.5, color: 'var(--ink-mute)', padding: '0 2px 8px' }}>
                {otherCount} more {otherCount === 1 ? 'link covers' : 'links cover'} other trips only.
                Manage {otherCount === 1 ? 'it' : 'them'} from the trips page.
                {isDraft && ' All-trips links do not include drafts.'}
              </div>
            )}
            {note && (
              <div style={{ fontSize: 12, color: 'var(--ink-soft)', padding: '2px 2px 8px' }}>{note}</div>
            )}
            {error && (
              <div style={{ fontSize: 12, color: 'var(--accent-deep)', padding: '2px 2px 8px' }}>{error}</div>
            )}
            {!creating ? (
              <Button variant="primary" size="sm" icon="plus" onClick={() => setCreating(true)} disabled={busy}>
                New guest link
              </Button>
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                  padding: 10,
                  borderRadius: 8,
                  border: '1px solid var(--line)',
                  background: 'var(--surface-2)',
                }}
              >
                {tripId && !moreScopes && (
                  <div style={{ fontSize: 12.5, fontFamily: 'var(--font-ui)' }}>
                    For {tripNames.get(tripId) ?? tripId} only
                    {scope === 'this' && (
                      <>
                        {' · '}
                        <button
                          onClick={() => {
                            setMoreScopes(true);
                            setScope('trips');
                          }}
                          style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--accent)', fontFamily: 'var(--font-ui)', padding: 0 }}
                        >
                          More options
                        </button>
                      </>
                    )}
                  </div>
                )}
                {(!tripId || moreScopes) && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {tripId && (
                      <label style={radioRow}>
                        <input type="radio" checked={scope === 'this'} onChange={() => setScope('this')} />
                        This trip only
                      </label>
                    )}
                    <label style={radioRow}>
                      <input type="radio" checked={scope === 'trips'} onChange={() => setScope('trips')} />
                      Pick trips
                    </label>
                    <label style={radioRow}>
                      <input type="radio" checked={scope === 'all'} onChange={() => setScope('all')} />
                      All my trips (future ones too; drafts stay hidden)
                    </label>
                  </div>
                )}
                {scope === 'trips' && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, paddingLeft: 2 }}>
                    {owned.map((t) => (
                      <label key={t.tripId} style={radioRow}>
                        <input
                          type="checkbox"
                          checked={picked.has(t.tripId)}
                          onChange={() =>
                            setPicked((prev) => {
                              const n = new Set(prev);
                              n.has(t.tripId) ? n.delete(t.tripId) : n.add(t.tripId);
                              return n;
                            })
                          }
                        />
                        {t.name}
                        {t.draft ? ' (draft)' : ''}
                      </label>
                    ))}
                  </div>
                )}
                <input
                  className="wa-input"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="Label (optional) — e.g. For Mom"
                  maxLength={80}
                  style={{
                    fontSize: 12.5,
                    padding: '7px 10px',
                    borderRadius: 8,
                    border: '1px solid var(--line)',
                    background: 'var(--bg)',
                    fontFamily: 'var(--font-ui)',
                    color: 'var(--ink)',
                  }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button variant="primary" size="sm" onClick={onCreate} disabled={busy}>
                    Create &amp; copy
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={resetCreate}
                    disabled={busy}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
            <div style={{ fontSize: 11, color: 'var(--ink-mute)', paddingTop: 8, lineHeight: 1.45 }}>
              Anyone with a link can view the covered trips — read-only.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
